// "Where it is" on a bought property's page (Section 2 continued), as in the pack: the area around
// it drawn the way the console's other maps are (CouncilMap / BuildingsMap: ONS boundaries, no
// basemap tiles), the property's district shaded navy with its name, the lime pin, counties,
// cities and towns named (public/maps/places.json, shared with the pack renderer), and the nearest
// cities and large towns with distance and direction. Positions are approximate: an indicator.
// Below it, as in the report (pack_maps.country_svg): Great Britain drawn as SVG with the property's
// pin and a box showing the area map's view, and the location's country and electricity network.
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Compass, MapPin } from "lucide-react";
import { SectionHead } from "./PackSections";
import { Labels, MAP, PIN_PATH, Proj, districtCode, loadMapData, nationOf, ringsPath, textWidth, type MapData } from "./svgMap";

interface Place {
  name: string;
  lat: number;
  lng: number;
  rank: number;
}
interface Places {
  places: Place[];
  counties: { name: string; lat: number; lng: number }[];
}

/** CSS custom property value (as CouncilMap). */
function token(name: string, fallback: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

// The console's pin: lime body, white edge, navy dot (as BuildingsMap)
const PIN = L.divIcon({
  className: "dmap-pin",
  html:
    '<svg viewBox="0 0 28 38" width="28" height="38" aria-hidden="true">' +
    '<path d="M14 1C7 1 1.5 6.4 1.5 13.3 1.5 22.5 14 37 14 37S26.5 22.5 26.5 13.3C26.5 6.4 21 1 14 1Z" fill="#E1EE2B" stroke="#fff" stroke-width="2"/>' +
    '<circle cx="14" cy="13.5" r="4.5" fill="#202937"/></svg>',
  iconSize: [28, 38],
  iconAnchor: [14, 37],
});

const DIRECTIONS = ["north", "north-east", "east", "south-east", "south", "south-west", "west", "north-west"];

/** The nearest cities and large towns: straight-line km and compass direction. */
function nearby(lat: number, lng: number, places: Place[], n = 5) {
  return places
    .filter((p) => p.rank <= 2)
    .map((p) => {
      const dy = (p.lat - lat) * 111.32;
      const dx = (p.lng - lng) * 111.32 * Math.cos((lat * Math.PI) / 180);
      const bearing = ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360;
      return { name: p.name, km: Math.hypot(dx, dy), dir: DIRECTIONS[Math.round(bearing / 45) % 8] };
    })
    .filter((p) => p.km >= 2)
    .sort((a, b) => a.km - b.km)
    .slice(0, n);
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

/** Great Britain with the major cities, the property's pin and a box showing the area map's view (pack_maps.country_svg). */
function CountryMap({ lat, lng, data, code, view }: { lat: number; lng: number; data: MapData; code: string | null; view: [number, number, number, number] | null }) {
  const W = 440,
    H = 640;
  const pr = useMemo(() => new Proj(49.9, -6.4, 58.7, 1.8, W, H), []);
  // The land and districts: drawn once per data load (the paths are large)
  const base = useMemo(
    () => (
      <>
        {(data.context?.features ?? []).map((f, i) => (
          <path key={`c${i}`} d={ringsPath(f.geometry, pr)} fill={MAP.land} stroke={MAP.edge} strokeWidth={0.6} />
        ))}
        {data.councils.features.map((f, i) =>
          f.properties?.code === code ? null : <path key={`d${i}`} d={ringsPath(f.geometry, pr)} fill={MAP.navy} fillOpacity={0.04} stroke={MAP.edge} strokeWidth={0.3} />,
        )}
        {data.councils.features
          .filter((f) => f.properties?.code === code)
          .map((f) => (
            <path key="own" d={ringsPath(f.geometry, pr)} fill={MAP.navy} fillOpacity={0.18} stroke="#000" strokeWidth={1.6} />
          ))}
      </>
    ),
    [data, pr, code],
  );
  const [px, py] = pr.xy(lat, lng);
  const labels = new Labels(W, H, [[px - 14, py - 38, px + 14, py + 2]]);
  // The area map's view, kept clear of city names
  const box = view ? [...pr.xy(view[2], view[1]), ...pr.xy(view[0], view[3])] : null;
  if (box) labels.boxes.push([box[0], box[1], box[2], box[3]]);
  const lbl = (key: string, x: number, y: number, text: string, size: number, weight: number, colour: string, anchor: "start" | "middle" | "end", spacing = 0) => (
    <text key={key} x={x} y={y} fontSize={size} fontWeight={weight} fill={colour} textAnchor={anchor} letterSpacing={spacing} fontFamily="Manrope, sans-serif" stroke="#fff" strokeWidth={3} strokeLinejoin="round" paintOrder="stroke">
      {text}
    </text>
  );
  const texts = (
    [
      ["SCOTLAND", 56.9, -4.4, 16],
      ["ENGLAND", 52.4, -1.3, 16],
      ["WALES", 52.25, -3.75, 14],
    ] as const
  ).map(([name, la, ln, size]) => {
    const [x, y] = pr.xy(la, ln);
    const tw = textWidth(name, size, 3);
    labels.fits(x - tw / 2, y - size, x + tw / 2, y + 2);
    return lbl(name, x, y, name, size, 800, "#8e959e", "middle", 3);
  });
  const marks: ReactElement[] = [];
  data.places.places
    .filter((p) => p.rank === 1)
    .forEach((p) => {
      const [x, y] = pr.xy(p.lat, p.lng);
      const tw = textWidth(p.name, 14);
      for (const [x1, anchor] of [
        [x + 5, "start"],
        [x - 5 - tw, "end"],
      ] as const) {
        if (labels.fits(x1, y - 11, x1 + tw, y + 4)) {
          marks.push(<circle key={`m-${p.name}`} cx={x} cy={y} r={3.6} fill={MAP.navy} stroke="#fff" strokeWidth={1.2} />);
          texts.push(lbl(`t-${p.name}`, anchor === "start" ? x + 5 : x - 5, y + 4.5, p.name, 14, 700, MAP.navy, anchor));
          break;
        }
      }
    });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Location in Great Britain">
      <rect width={W} height={H} fill={MAP.sea} />
      {base}
      {marks}
      {texts}
      {box && <rect x={box[0]} y={box[1]} width={box[2] - box[0]} height={box[3] - box[1]} fill={MAP.lime} fillOpacity={0.25} stroke={MAP.navy} strokeWidth={1.8} strokeDasharray="5 3" />}
      <g transform={`translate(${px - 14},${py - 37})`}>
        <path d={PIN_PATH} fill={MAP.lime} stroke={MAP.navy} strokeWidth={1.5} />
        <circle cx={14} cy={13.5} r={4.5} fill={MAP.navy} />
      </g>
    </svg>
  );
}

export function LocationMap({
  lat,
  lng,
  district,
  address,
  nation,
  gridOperator,
}: {
  lat: number;
  lng: number;
  district?: string | null;
  address: string;
  /** The nation, when the API knows it (the tax block, from the postcode); else from the district's code */
  nation?: string | null;
  /** The electricity network operator, when the grid check covers the area */
  gridOperator?: string | null;
}) {
  const el = useRef<HTMLDivElement>(null);
  const [places, setPlaces] = useState<Places | null>(null);
  const [data, setData] = useState<MapData | null>(null);
  // The area map's current view (south, west, north, east), shown as the box on the country map
  const [view, setView] = useState<[number, number, number, number] | null>(null);

  useEffect(() => {
    if (!el.current) return;
    const m = L.map(el.current, { zoomControl: true, minZoom: 5, maxZoom: 12, zoomSnap: 0.25, scrollWheelZoom: false }).setView([lat, lng], 8.75);
    m.attributionControl.setPrefix(false);
    m.attributionControl.addAttribution("Boundaries: ONS, OGL v3.0. Contains OS data © Crown copyright 2026");
    let cancelled = false; // StrictMode mounts twice
    const track = () => {
      const bd = m.getBounds();
      if (!cancelled) setView([bd.getSouth(), bd.getWest(), bd.getNorth(), bd.getEast()]);
    };
    m.on("moveend", track);
    track();

    // The same files as the other maps on the page, fetched once (svgMap.loadMapData)
    loadMapData()
      .then((d) => {
        if (cancelled) return;
        const { context, councils, places: named } = d;
        setData(d);
        setPlaces(named);
        if (context)
          L.geoJSON(context as GeoJSON.FeatureCollection, {
            interactive: false,
            style: () => ({ weight: 1, color: token("--brand-border-strong", "#9a9a9a"), fillColor: token("--brand-border", "#e5e4e2"), fillOpacity: 0.7 }),
          }).addTo(m);
        // Every district faint; the property's own shaded navy with a black edge and its name
        const own: L.Layer[] = [];
        L.geoJSON(councils, {
          interactive: false,
          onEachFeature: (f, layer) => {
            if (f.properties?.name !== district) return;
            own.push(layer);
            (layer as L.Path).bindTooltip(esc(String(f.properties.name)), { permanent: true, direction: "center", className: "dmap-label", interactive: false });
          },
          style: (f) => {
            const mine = f?.properties?.name === district;
            return {
              weight: mine ? 2 : 1,
              color: mine ? "#000000" : token("--brand-text-tertiary", "#8e959e"),
              fillColor: token("--brand-primary", "#202937"),
              fillOpacity: mine ? 0.18 : 0.04,
            };
          },
        }).addTo(m);
        own.forEach((l) => (l as L.Path).bringToFront());

        // Counties (grey capitals) when zoomed out; cities and towns as dots with names; towns only
        // when zoomed in, so the area view stays readable
        const counties = L.layerGroup(
          named.counties.map((c) =>
            L.marker([c.lat, c.lng], { interactive: false, keyboard: false, icon: L.divIcon({ className: "lmap-county", html: esc(c.name), iconSize: [0, 0] }) }),
          ),
        );
        const dot = (p: Place) =>
          L.marker([p.lat, p.lng], {
            interactive: false,
            keyboard: false,
            icon: L.divIcon({ className: `lmap-place is-rank${p.rank}`, html: `<i></i><span>${esc(p.name)}</span>`, iconSize: [0, 0] }),
          });
        // Not the places right by the property: the pin and the district's name already say where it is
        const clear = (p: Place) => Math.hypot((p.lat - lat) * 111.32, (p.lng - lng) * 111.32 * Math.cos((lat * Math.PI) / 180)) >= 4;
        const cities = L.layerGroup(named.places.filter((p) => p.rank <= 2 && clear(p)).map(dot)).addTo(m);
        const towns = L.layerGroup(named.places.filter((p) => p.rank === 3 && clear(p)).map(dot));
        const byZoom = () => {
          const z = m.getZoom();
          if (z >= 9.5) towns.addTo(m);
          else towns.remove();
          if (z <= 9.75) counties.addTo(m);
          else counties.remove();
          cities.addTo(m);
        };
        m.on("zoomend", byZoom);
        byZoom();
        L.marker([lat, lng], { icon: PIN, title: "The property", keyboard: false, zIndexOffset: 1000 }).addTo(m);
      })
      .catch(() => null);

    return () => {
      cancelled = true;
      m.remove();
    };
  }, [lat, lng, district]);

  const near = places ? nearby(lat, lng, places.places) : [];
  const code = data ? districtCode(data.councils, district) : null;
  const country = nation ?? nationOf(code);
  return (
    <section className="card eo" aria-labelledby="pk-where">
      <SectionHead id="pk-where" kicker="Section 2 continued" icon={MapPin} title="Where it is" sub="The area around the property, with its district shaded." />
      <figure className="lmap-area">
        <div ref={el} className="lmap-canvas" role="img" aria-label="Map of the area around the property" />
        <figcaption className="muted eo-note">
          {district ? `The ${district} district shaded. ` : ""}Indicative: positions approximate.
        </figcaption>
      </figure>
      <div className="lmap-grid">
        {/* Great Britain, with the area map's view as a box */}
        <figure className="lmap-country">
          {data ? <CountryMap lat={lat} lng={lng} data={data} code={code} view={view} /> : <div className="lmap-country-wait muted">Loading the map…</div>}
          <figcaption className="muted eo-note">{country ? `${country}, United Kingdom` : "United Kingdom"}. The shaded box is the map above.</figcaption>
        </figure>
        <div className="lmap-side">
          <div className="pk-panel">
            <h3 className="pk-sub">
              <MapPin size={16} aria-hidden="true" /> Location
            </h3>
            <dl className="building-facts">
              <div>
                <dt>Address</dt>
                <dd>{address}</dd>
              </div>
              {district && (
                <div>
                  <dt>Local authority</dt>
                  <dd>{district}</dd>
                </div>
              )}
              {country && (
                <div>
                  <dt>Country</dt>
                  <dd>{country}</dd>
                </div>
              )}
              {gridOperator && (
                <div>
                  <dt>Electricity network</dt>
                  <dd>{gridOperator}</dd>
                </div>
              )}
            </dl>
          </div>
          {near.length > 0 && (
            <div className="pk-panel">
              <h3 className="pk-sub">
                <Compass size={16} aria-hidden="true" /> Nearby
              </h3>
              <ul className="lmap-near">
                {near.map((n) => (
                  <li key={n.name}>
                    <span>{n.name}</span>
                    <b>
                      {Math.round(n.km)} km {n.dir}
                    </b>
                  </li>
                ))}
              </ul>
              <p className="muted eo-note">Straight-line distances to the nearest cities and large towns.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
